import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { FolderSLAUserMetrics } from "../types/folderSla.types";

interface FolderSLAUserChartProps {
  data: FolderSLAUserMetrics[];
  loading?: boolean;
}

// Safe accessor with default value
const safeNumber = (value: number | undefined | null, defaultValue = 0): number => {
  return typeof value === "number" && !isNaN(value) ? value : defaultValue;
};

const FolderSLAUserChart: React.FC<FolderSLAUserChartProps> = ({
  data,
  loading = false,
}) => {
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-pulse text-gray-400">Cargando...</div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-gray-500">No hay datos de usuarios disponibles</p>
      </div>
    );
  }

  // Filter users with actual processed folders and prepare chart data
  const chartData = data
    .filter((user) => safeNumber(user.total_processed) > 0)
    .map((user) => {
      const totalProcessed = safeNumber(user.total_processed);
      const totalNonCompliant = safeNumber(user.total_non_compliant);
      const nonCompliantPct = totalProcessed > 0
        ? (totalNonCompliant / totalProcessed) * 100
        : 0;

      return {
        name: user.user_name || "Usuario",
        "Antes del Corte": safeNumber(user.before_cutoff_compliance_pct),
        "Después del Corte": safeNumber(user.after_cutoff_compliance_pct),
        "Fuera de SLA": nonCompliantPct,
        Cumplimiento: safeNumber(user.overall_compliance_pct),
        totalProcessed: totalProcessed,
        totalCompliant: safeNumber(user.total_compliant),
        totalNonCompliant: totalNonCompliant,
        avgHours: safeNumber(user.avg_completion_time_business_hours),
        email: user.user_email || "",
      };
    });

  if (chartData.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-gray-500">
          No hay usuarios con expedientes procesados
        </p>
      </div>
    );
  }

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const tooltipData = payload[0].payload;
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-900">{label}</p>
          <p className="text-xs text-gray-600 mb-2">{tooltipData.email}</p>
          <div className="space-y-1">
            <p className="text-sm text-green-600">
              Cumplimiento: {safeNumber(tooltipData.Cumplimiento).toFixed(1)}%
            </p>
            <p className="text-sm text-emerald-600">
              Antes del Corte: {safeNumber(tooltipData["Antes del Corte"]).toFixed(1)}%
            </p>
            <p className="text-sm text-blue-600">
              Después del Corte: {safeNumber(tooltipData["Después del Corte"]).toFixed(1)}%
            </p>
            <p className="text-sm text-red-600">
              Fuera de SLA: {safeNumber(tooltipData["Fuera de SLA"]).toFixed(1)}%
            </p>
            <div className="border-t pt-2 mt-2 text-xs text-gray-600">
              <p>Total Procesados: {tooltipData.totalProcessed}</p>
              <p>En SLA: {tooltipData.totalCompliant}</p>
              <p>Fuera SLA: {tooltipData.totalNonCompliant}</p>
              <p>Tiempo Promedio: {tooltipData.avgHours.toFixed(1)}h</p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart
        data={chartData}
        margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
        barCategoryGap="20%"
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 12 }}
          interval={0}
          angle={-45}
          textAnchor="end"
          height={80}
        />
        <YAxis
          tick={{ fontSize: 12 }}
          domain={[0, 100]}
          tickFormatter={(value) => `${value}%`}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        <Bar
          dataKey="Cumplimiento"
          fill="#10b981"
          name="Cumplimiento"
          radius={[4, 4, 0, 0]}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default FolderSLAUserChart;
